import { useState } from "react";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Glyph, Modal } from "../../components/ui";
import { Button, Tag } from "../../components/ui/ds";
import { LIFE_CAT } from "../../lib/constants";
import { joinedLabel } from "../../lib/format";
import { SK_MONTHS_GEN } from "../../lib/date-sk";
import "./global-launch.css";

// In-app „launch" modal novej globálnej dávky (napr. „Zrkadlová dvojtýždňovka") — ukáže sa RAZ,
// keď je dávka odoslaná e-mailom (s.global_launch z /api/state) a user ešte nezareagoval. Stavaný
// na zdieľanom Modal-e (rovnako ako ChallengeInfoModal), plne tématizovaný (žiadne surové farby):
// flame ikona v hlavičke, zrkadlová ilustrácia v tóne značky, „Deň X zo Y" a KAŽDÁ výzva má vlastné
// tlačidlo „Zapojiť sa". Zavretie rieši rodič (dismiss + toast „nájdeš v Priatelia → Prebiehajúce výzvy").

// **tučné** v intro texte → <strong>
function renderIntro(text) {
  return (text || "").split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

// prvé písmeno veľké (intro sa niekedy uloží s malým začiatkom)
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Zrkadlo v tóne značky (bez hnedej/zlatej) — odvodené z --brand-primary derivácií. viewBox má hore
// dosť miesta, aby sa horný okraj rámu neorezal.
function MirrorArt() {
  return (
    <div className="bz-launch__art">
      <svg width="104" height="140" viewBox="0 0 104 140" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="bzMirGlass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--brand-primary-surface-strong)" />
            <stop offset="1" stopColor="var(--brand-primary-surface)" />
          </linearGradient>
        </defs>
        <rect x="48" y="96" width="8" height="30" rx="3" fill="var(--brand-primary)" />
        <ellipse cx="52" cy="130" rx="18" ry="5" fill="var(--brand-primary)" opacity="0.45" />
        <ellipse cx="52" cy="52" rx="40" ry="46" fill="none" stroke="var(--brand-primary)" strokeWidth="6" />
        <ellipse cx="52" cy="52" rx="34" ry="40" fill="url(#bzMirGlass)" />
        <path d="M40 40 q10 -8 22 -2" stroke="var(--neutral-surface)" strokeWidth="4" strokeLinecap="round" opacity="0.7" fill="none" />
      </svg>
    </div>
  );
}

// Augustová výdrž (art='august') — plameniak stojaci na jednej nohe vo vode, slnko za ním.
// Farby idú v TÓNE TÉMY (tokeny --launch-art-*, derivácie --brand-primary), rovnako ako zrkadlo.
function FlamingoArt() {
  return (
    <div className="bz-launch__art">
      <svg width="140" height="140" viewBox="0 0 140 140" fill="none" aria-hidden="true">
        {/* slnko za plameniakom */}
        <circle className="bz-launch__sun" cx="104" cy="32" r="19" fill="var(--launch-art-sun)" opacity="0.9" />
        {/* breh + vlnky na hladine */}
        <path d="M6 124 H134" stroke="var(--launch-art-ground)" strokeWidth="4" strokeLinecap="round" />
        <path className="bz-launch__ripple" d="M28 133 h30" stroke="var(--launch-art-ground)" strokeWidth="3" strokeLinecap="round" opacity="0.55" />
        <path className="bz-launch__ripple bz-launch__ripple--2" d="M76 133 h28" stroke="var(--launch-art-ground)" strokeWidth="3" strokeLinecap="round" opacity="0.45" />
        {/* stojaca noha (zalomená v kolene) + druhá, pokrčená pod telom */}
        <path d="M62 97 L62 110 L57 124" stroke="var(--launch-art-ink)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M55 97 L44 104 L51 110" stroke="var(--launch-art-ink)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {/* chvost (za telom) + telo */}
        <path d="M76 78 C86 72 92 69 99 64 C94 75 88 84 82 91 Z" fill="var(--launch-art-ink)" />
        <ellipse cx="58" cy="86" rx="22" ry="14" fill="var(--launch-art-ink)" />
        {/* krk (S-krivka), hlava, zobák a oko — hlava mieri dolu do knihy */}
        <path d="M48 79 C37 69 37 50 50 45 C59 41 68 46 67 55" stroke="var(--launch-art-ink)" strokeWidth="7" strokeLinecap="round" fill="none" />
        <circle cx="67" cy="56" r="7.5" fill="var(--launch-art-ink)" />
        <path d="M72 59 L83 65 L71 65 Z" fill="var(--launch-art-sun)" />
        <circle cx="65" cy="53" r="1.7" fill="var(--neutral-surface)" />
        {/* otvorená kniha — tretia augustová súťaž je pre hlavu, nie pre telo */}
        <path d="M74 97 C82 92 90 92 96 96 L96 111 C90 107 82 107 74 112 Z" fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M118 97 C110 92 102 92 96 96 L96 111 C102 107 110 107 118 112 Z" fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M80 100 h10 M80 104 h9" stroke="var(--launch-art-ground)" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M102 100 h10 M103 104 h9" stroke="var(--launch-art-ground)" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </div>
  );
}

// „Deň X zo Y" (dávka už beží) alebo „Štart 1. augusta · do 31. augusta" (aktivovaná deň vopred).
function metaLine(launch) {
  const dayOf = (iso) => {
    const p = (iso || "").split("-");
    if (p.length < 3) return "";
    return `${parseInt(p[2], 10)}. ${SK_MONTHS_GEN[parseInt(p[1], 10) - 1] || ""}`;
  };
  if ((launch.starts_in || 0) > 0) {
    const start = dayOf(launch.start_day), end = dayOf(launch.end_day);
    return (
      <>
        {launch.starts_in === 1 ? "Začíname zajtra"
          : `Začíname o ${launch.starts_in} ${launch.starts_in < 5 ? "dni" : "dní"}`}
        {start && <> · <span className="bz-launch__metaStrong">{start}{end ? ` – ${end}` : ""}</span></>}
      </>
    );
  }
  return `Deň ${launch.day} zo ${launch.total} · nech ti séria rastie`;
}

// Podtitulok podľa obsahu dávky: „Nová mesačná súťaž" / „Dve nové dvojtýždňové výzvy" …
function kindLabel(chs, total) {
  const n = chs.length;
  const comp = n > 0 && chs.every((c) => c.is_competition);
  const period = total >= 28 ? "mesačn" : total >= 14 ? "dvojtýždňov" : "týždňov";
  if (n === 1) return `Nová ${period}á ${comp ? "súťaž" : "výzva"}`;
  const word = { 2: "Dve", 3: "Tri", 4: "Štyri" }[n] || n;
  return `${word} nové ${period}é ${comp ? "súťaže" : "výzvy"}`;
}

// Septembrová dvojka (art='september') — postava v planku, pred ňou otvorená kniha a nad tým
// padajúce listy. Rovnaká farebná reč ako ostatné ilustrácie: iba tokeny --launch-art-*.
// (Dávka nemá plameniaka, tak by augustová kresba klamala o tom, do čoho sa človek zapája.)
function SeptemberArt() {
  return (
    <div className="bz-launch__art">
      <svg width="140" height="140" viewBox="0 0 140 140" fill="none" aria-hidden="true">
        {/* padajúce listy — jediný pohyb v obrázku, jeseň bez slnka.
            Obrys v `ink` (ako pri knihe) je tu nutnosť, nie ozdoba: `--launch-art-sun` je len
            48 % odtieň témy, takže samotná výplň sa v svetlých témach na bielom stratí. */}
        <g className="bz-launch__leaf">
          <path d="M104 26 C114 28 118 39 111 47 C102 44 99 33 104 26 Z"
            fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2.2" strokeLinejoin="round" />
          <path d="M105 29 L110 44" stroke="var(--launch-art-ink)" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
        </g>
        <g className="bz-launch__leaf bz-launch__leaf--2">
          <path d="M121 53 C129 55 131 63 126 69 C119 66 118 58 121 53 Z"
            fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2" strokeLinejoin="round" />
        </g>
        {/* zem */}
        <path d="M6 120 H134" stroke="var(--launch-art-ground)" strokeWidth="4" strokeLinecap="round" />
        {/* otvorená kniha pred hlavou — číta a zároveň drží plank (obe septembrové súťaže naraz) */}
        <path d="M8 96 C17 90 25 90 30 95 L30 113 C25 108 17 108 8 114 Z" fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2.6" strokeLinejoin="round" />
        <path d="M52 96 C43 90 35 90 30 95 L30 113 C35 108 43 108 52 114 Z" fill="var(--launch-art-sun)" stroke="var(--launch-art-ink)" strokeWidth="2.6" strokeLinejoin="round" />
        <path d="M13 100 h10 M13 105 h9" stroke="var(--launch-art-ground)" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M36 100 h10 M36 105 h9" stroke="var(--launch-art-ground)" strokeWidth="1.8" strokeLinecap="round" />
        {/* predlaktinový plank zboku: predlaktie na zemi → lakeť → plece nad ním */}
        <path d="M62 114 H84" stroke="var(--launch-art-ink)" strokeWidth="7" strokeLinecap="round" />
        <path d="M84 114 L88 96" stroke="var(--launch-art-ink)" strokeWidth="7" strokeLinecap="round" />
        {/* trup a nohy — takmer priamka od pleca po pätu, to je celý plank */}
        <path d="M88 96 L114 105" stroke="var(--launch-art-ink)" strokeWidth="11" strokeLinecap="round" />
        <path d="M114 105 L131 113" stroke="var(--launch-art-ink)" strokeWidth="9" strokeLinecap="round" />
        <path d="M131 113 L133 119" stroke="var(--launch-art-ink)" strokeWidth="6" strokeLinecap="round" />
        {/* krk + hlava nad knihou */}
        <path d="M88 96 L79 93" stroke="var(--launch-art-ink)" strokeWidth="7" strokeLinecap="round" />
        <circle cx="70" cy="92" r="10" fill="var(--launch-art-ink)" />
      </svg>
    </div>
  );
}

export function GlobalLaunchModal({ launch, onClose, onJoin }) {
  const chs = launch.challenges || [];
  // ladenie dávky (art) — iná ilustrácia (farby vždy z témy)
  const september = launch.art === "september";
  const august = launch.art === "august";
  // per-výzva busy stav — inak by sa „…" ukázalo na OBOCH tlačidlách (zdieľaný joinBusy)
  const [busyCid, setBusyCid] = useState(null);
  return (
    <Modal center
      iconNode={<BuzerIcon name={(august || september) ? "trophy" : "flame"} size={20} color="var(--brand-primary)" />}
      title={launch.title}
      subtitle={<span style={{ fontFamily: "var(--ff-ui)", textTransform: "none", letterSpacing: "normal", fontSize: 12.5, fontWeight: 600 }}>{kindLabel(chs, launch.total)}</span>}
      onClose={onClose}
      footer={<Button variant="ghost" block onClick={onClose}>Zavrieť</Button>}>
      {launch.intro && (
        <div className="bz-aitext" style={{ margin: "0 0 4px" }}>
          {renderIntro(cap(launch.intro))}
        </div>
      )}
      {september ? <SeptemberArt /> : august ? <FlamingoArt /> : <MirrorArt />}
      <div className="bz-launch__meta">{metaLine(launch)}</div>
      {chs.map((c) => {
        const lc = c.life_cat ? LIFE_CAT[c.life_cat] : null;
        return (
          <div key={c.cid} style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "var(--neutral-surface-2)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-lg)", padding: "12px 13px", marginTop: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: "var(--radius-md)", background: "var(--neutral-surface)", border: "1px solid var(--neutral-border-strong)", flexShrink: 0 }}>
              <Glyph icon={c.icon} size={18} fallback="challenge" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--neutral-text-main)", lineHeight: 1.3 }}>{c.name}</div>
              <div style={{ fontSize: 13, color: "var(--neutral-text-muted)", lineHeight: 1.45, marginTop: 2 }}>{c.descr}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {/* pri súťaži sú body cena pre víťaza, nie odmena za každý deň */}
                <Tag tone="brand" size="xs" bare>+{c.points}{c.is_competition ? " víťazovi" : ""}</Tag>
                <Tag tone="neutral" size="xs">{c.is_competition ? "súťaž" : "denne"}</Tag>
                {lc && <Tag tone="neutral" size="xs" icon={lc.icon}>{lc.label}</Tag>}
              </div>
              <div style={{ marginTop: 10 }}>
                {c.joined ? (
                  <Button variant="ghost" size="sm" disabled style={{ color: "var(--brand-primary)" }}>
                    <Icon name="check" size={15} color="currentColor" stroke={2.6} />{joinedLabel()}
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" disabled={busyCid === c.cid}
                    onClick={async () => { setBusyCid(c.cid); try { await onJoin(c.cid); } finally { setBusyCid(null); } }}>
                    {busyCid === c.cid ? "…" : "Zapojiť sa"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </Modal>
  );
}
